// Start a plan (arc 101 slice 07) — `POST /v1/plans`, the scout entry. The one
// WRITE on the epics surface; `lib/plan.ts` keeps the two reads, the split
// `lib/tasks.ts` / `lib/task-dispatch.ts` already draws.
//
// A 202 IS NOT A LIVE SESSION, and this whole module exists to keep the screen
// from saying otherwise. The route answers a TASK id and nothing else: a box
// mints the run id when it CLAIMS the task (`mintRunId`, `apps/runner/src/loop.ts`)
// and streams it back, so `tasks.run_id` is null until then and there is no run
// to steer, tail or link to yet. Dispatched-then-claimed is the shape the result
// renders; a run id here could only have been invented.
//
// NOR DOES A PLAN EXIST YET. The scout converses first, and `plan_authored`
// lands the `plans`/`plan_groups` rows minutes later
// (`apps/worker/src/fleet/scout-plan-ingest.ts`) — which is why nothing on this
// path re-reads the epics index. Refetching would ask the server to confirm that
// nothing happened, the rule `<NewTaskModal/>` holds for an escalated ask.
//
// THE REFUSALS ARE THE SERVER'S. A 404 (repo not this account's, or not enabled)
// and a 409 (`no_scout_capacity` — no online box in `universal` mode) both carry
// the port's own sentence, shared with the `plan_start` MCP verb so the two doors
// diagnose one state in one wording. The 409 is ONE status over six graded
// conditions (`ScoutCapacityGap`, arc 101 slice 06): the server picks the sentence
// from the one that failed, so it already says whether waiting clears it. This file
// frames a refusal by status and prints that sentence underneath; it re-derives
// neither, and must not start branching on the wording to guess the condition.

import { PLAN_PROMPT_MAX_CHARS } from '@developerz/domain';
import { ApiError } from './api';
import { type ApiCall, sendApiCall } from './api-call';
import { SESSION_TOKEN } from './session';

/** What the form collects — exactly the two fields `PlanStartInputSchema` takes. */
export type PlanStartInput = {
  /** `owner/repo`, enabled for this account. Picked from the repo list, never typed. */
  readonly repo: string;
  /** The raw ask; the scout turns it into groups and tasks. */
  readonly prompt: string;
};

/**
 * The ask's ceiling — {@link PLAN_PROMPT_MAX_CHARS}, the domain constant
 * `PlanStartInputSchema.prompt` (`packages/mcp/src/tools.ts`) cites, imported from
 * `@developerz/domain` because `@developerz/mcp` is not a dashboard dependency. A
 * hint on the textarea, never an authorization: the server is the arbiter and its
 * 400 is what a longer ask actually reads.
 */
export const PLAN_PROMPT_MAX = PLAN_PROMPT_MAX_CHARS;

/** What `POST /v1/plans` did, as this client can honestly report it. */
export type PlanStartOutcome =
  /** 202: the scout task is dispatched. Its `run_id` is null until a box claims it. */
  | { readonly kind: 'started'; readonly task_id: string }
  /** A 2xx that named no task — see {@link readStartOutcome}. */
  | { readonly kind: 'unclear' }
  /** Any non-2xx: `status` frames the copy, `detail` is the server's own sentence. */
  | { readonly kind: 'refused'; readonly status: number; readonly detail: string | null };

/**
 * The start as ONE value — fired by {@link startPlan}, printed by
 * `<CopyAsCall/>`. `plan_start` is a FLAT tool (no `action` argument), which is
 * why the verb carries no dot: `mcpRequestBody` reads that as `tools/call` with
 * the arguments straight, with no `action` to derive.
 */
export function planStartCall(input: PlanStartInput): ApiCall {
  const body = { repo: input.repo, prompt: input.prompt };
  return {
    capability: 'plan.start',
    method: 'POST',
    path: '/v1/plans',
    body,
    mcp: { kind: 'verb', verb: 'plan_start', arguments: body },
  };
}

/**
 * A 2xx names a dispatch only when it names a TASK. A 204, a proxy-injected 200
 * and a body shaped some other way all leave the caller unable to say what the
 * fleet was handed, so they answer `unclear` rather than claim a scout session —
 * the discriminator-not-default-branch rule `readOutcome` holds for dispatch.
 */
export function readStartOutcome(body: unknown): PlanStartOutcome {
  const taskId =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)['task_id']
      : undefined;
  return typeof taskId === 'string' && taskId !== ''
    ? { kind: 'started', task_id: taskId }
    : { kind: 'unclear' };
}

/**
 * Dispatch the scout session. Never throws: a refusal IS an outcome (a 401 has
 * already latched the session-expiry gate in `apiFetch`), and a dead network has
 * no status, so it answers 0 and reads as the generic line.
 */
export async function startPlan(
  input: PlanStartInput,
  token: string | null = SESSION_TOKEN,
): Promise<PlanStartOutcome> {
  try {
    return readStartOutcome(await sendApiCall<unknown>(planStartCall(input), token));
  } catch (err) {
    if (err instanceof ApiError) {
      return { kind: 'refused', status: err.status, detail: err.detail ?? null };
    }
    return { kind: 'refused', status: 0, detail: null };
  }
}

/**
 * Copy key framing a refusal. Only the statuses whose STATUS is the whole reason
 * are named: a 404 is the repo (folded on purpose server-side, so a foreign repo
 * is never confirmed to exist), a 409 is the fleet, a 403 is the scope. Everything
 * else — a 400 on the ask, a 429, a 5xx, a dead network — is generic, and
 * `<StartPlanResult/>` prints the API's own `detail` under whichever line it picks.
 */
const REFUSAL_KEYS: Record<number, string> = {
  403: 'fleet.plans.start.refused_scope',
  404: 'fleet.plans.start.refused_repo',
  409: 'fleet.plans.start.refused_capacity',
};

export const planRefusalKey = (status: number): string =>
  REFUSAL_KEYS[status] ?? 'fleet.plans.start.refused_other';
